import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Loader2, Check, X, Sparkles, Coins, ArrowRight } from "lucide-react";

const POLL_LIMIT = 20;
const POLL_MS = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ status: "polling", detail: null });
  const attempts = useRef(0);
  const session_id = params.get("session_id");

  useEffect(() => {
    if (!session_id) { setState({ status: "error", detail: "No session id in URL." }); return; }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", detail: data });
          checkAuth();
          if (data.lookup_key === "qr_lifetime_pass") {
            toast.success("QR lifetime pass unlocked — no watermark, unlimited HD.");
            // Fast redirect so the QR Generator can auto-trigger the HD download.
            // Explicit flag + session_id so the generator can show the gold confirmation pill.
            setTimeout(() => navigate(`/qr-generator?paid=qr&session_id=${encodeURIComponent(session_id)}`), 900);
          } else if (data.lookup_key === "dynamic_qr_activation") {
            toast.success("Dynamic QR activated — your first HD download is on us.");
            setTimeout(() => navigate(`/qr-generator?paid=dynamic&session_id=${encodeURIComponent(session_id)}`), 900);
          } else {
            toast.success("Payment received — credits added.");
          }
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setState({ status: "failed", detail: data });
          return;
        }
      } catch (e) {
        // 404 — keep polling briefly, then fail
      }
      if (attempts.current >= POLL_LIMIT) {
        setState({ status: "timeout", detail: null });
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [session_id, checkAuth, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-24">
        <div className="p-8 md:p-12 rounded-3xl bg-[#120E15] border border-[#F2C94C]/20 text-center" data-testid="payment-success-card">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-14 h-14 text-[#F2C94C] mx-auto mb-6 animate-spin" />
              <h1 className="font-display text-3xl md:text-4xl text-white mb-3">Confirming payment…</h1>
              <p className="font-body text-white">Talking to Stripe. This usually takes just a moment.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-[#27AE60]/20 border border-[#27AE60] flex items-center justify-center mx-auto mb-6 glow-cyan">
                <Check className="w-8 h-8 text-[#27AE60]" strokeWidth={3} />
              </div>
              <h1 className="font-display text-3xl md:text-4xl text-white mb-3">
                {state.detail?.lookup_key === "qr_lifetime_pass" ? "QR lifetime pass unlocked" : "You're topped up ⚡"}
              </h1>
              <p className="font-body text-white mb-6">
                {state.detail?.lookup_key === "qr_lifetime_pass"
                  ? "Unlimited HD, no watermark — forever. Redirecting to the QR Generator…"
                  : (state.detail?.lookup_key?.includes("monthly") ? "Subscription active." : "Credits added to your vault.") + " Time to create."}
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {state.detail?.lookup_key === "qr_lifetime_pass" ? (
                  <button onClick={() => navigate("/qr-generator?paid=qr")} data-testid="go-qr-btn"
                    className="font-mono-hy text-xs px-6 py-3 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Open QR Generator <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button onClick={() => navigate("/studio")} data-testid="go-studio-btn"
                    className="font-mono-hy text-xs px-6 py-3 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Open Studio <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => navigate("/pricing")} data-testid="back-pricing-btn"
                  className="font-mono-hy text-xs px-6 py-3 rounded-full border border-[#F2C94C]/25 text-[#F2C94C] hover:bg-[#F2C94C]/10 transition-all">
                  Buy more
                </button>
              </div>
            </>
          )}
          {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-[#E27D60]/20 border border-[#E27D60] flex items-center justify-center mx-auto mb-6">
                <X className="w-8 h-8 text-[#E27D60]" strokeWidth={3} />
              </div>
              <h1 className="font-display text-3xl md:text-4xl text-white mb-3">
                {state.status === "timeout" ? "Still confirming…" : "Payment didn't complete"}
              </h1>
              <p className="font-body text-white mb-6">
                {state.status === "timeout"
                  ? "Stripe is taking longer than usual. Check back in a few minutes — you won't be double-charged."
                  : "No charge was made. Try again or contact support if you were billed."}
              </p>
              <button onClick={() => navigate("/pricing")} data-testid="back-pricing-btn"
                className="font-mono-hy text-xs px-6 py-3 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all">
                Back to Pricing
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
